import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormControlLabel,
  Switch,
  IconButton,
  Autocomplete,
  styled,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { addBankAccount } from '../api/services';
import bankData from '../data/bankData.json'; 

// iOS-style Switch Component - Simplified
const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#65C466',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const AddBankAccount = ({ open, onClose, onSuccess }) => {
  const [isBankAccount, setIsBankAccount] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    bankName: '',
    branchCode: '',
    accountNumber: '',
    accountType: '',
    accountCurrency: '',
    mobileNumber: '',
    mobileSystem: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const accountTypes = ['Current', 'Savings'];
  const currencies = ['KES', 'XAF', 'RWF', 'USD'];
  const mobileMoneySystems = [
    'MPESA',
    'Airtel Money',
    'Orange Money',
    'MTN Mobile Money'
  ];

  // Map mobile money systems to their default currencies
  const mobileMoneyCurrencies = {
    'MPESA': 'KES',
    'Airtel Money': 'KES',
    'Orange Money': 'XAF',
    'MTN Mobile Money': 'XAF'
  };

  // Handle mobile system change to auto-set currency
  const handleMobileSystemChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      mobileSystem: value,
      accountCurrency: mobileMoneyCurrencies[value] || '' 
    }));
    setErrors((prev) => ({ ...prev, mobileSystem: '' }));
    setApiError(null);
  };

  // Handle toggle change
  const handleToggleChange = (event) => {
    setIsBankAccount(event.target.checked);
    // Reset form data when switching between bank and mobile money
    setFormData({
      fullName: formData.fullName, // Keep the name
      bankName: '',
      branchCode: '',
      accountNumber: '',
      accountType: '',
      accountCurrency: '',
      mobileNumber: '',
      mobileSystem: '',
    });
    setErrors({});
    setApiError(null);
  };

  // Updates form data and clears related errors
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setApiError(null);
  };

  // Handle autocomplete change for bank name
  const handleBankNameChange = (event, newValue) => {
    setFormData((prev) => ({ ...prev, bankName: newValue || '' }));
    setErrors((prev) => ({ ...prev, bankName: '' }));
    setApiError(null);
  };

  // Validates form fields, returns true if valid
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName) {
      newErrors.fullName = 'Please enter your full name.';
    } else if (!/^[a-zA-Z\s]{2,}$/.test(formData.fullName)) {
      newErrors.fullName = 'Full name should contain only letters and spaces (minimum 2 characters).';
    }

    if (isBankAccount) {
      if (!formData.bankName) {
        newErrors.bankName = 'Please select a financial institution.';
      }

      if (!formData.branchCode) {
        newErrors.branchCode = 'Please enter the branch code.';
      } else if (formData.branchCode.length < 2) {
        newErrors.branchCode = 'Branch code must be at least 2 characters long.';
      }

      if (!formData.accountNumber) {
        newErrors.accountNumber = 'Please enter the account number.';
      } else if (!/^\d+$/.test(formData.accountNumber)) {
        newErrors.accountNumber = 'Account number must contain only digits.';
      } else if (formData.accountNumber === '0' || parseInt(formData.accountNumber, 10) <= 0) {
        newErrors.accountNumber = 'Account number cannot be zero or negative.';
      } else if (formData.accountNumber.length < 4) {
        newErrors.accountNumber = 'Account number must be at least 4 digits long.';
      }

      if (!formData.accountType) {
        newErrors.accountType = 'Please select an account type.';
      }

      if (!formData.accountCurrency) {
        newErrors.accountCurrency = 'Please select a currency.';
      }
    } else {
      // Mobile Money validation
      if (!formData.mobileSystem) {
        newErrors.mobileSystem = 'Please select a mobile money system.';
      }

      if (!formData.mobileNumber) {
        newErrors.mobileNumber = 'Please enter a mobile number for Mobile Money.';
      } else if (!/^\+?\d{9,15}$/.test(formData.mobileNumber.replace(/\s/g, ''))) {
        newErrors.mobileNumber = 'Please enter a valid mobile number (9-15 digits, optional + prefix).';
      }

      // Currency is auto-set but still validate it's there
      if (!formData.accountCurrency) {
        newErrors.accountCurrency = 'Currency is required.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submits form data to API
  const handleSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);
    setApiError(null);

    try {
      // Prepare data for API
      const submitData = {
        ...formData,
        // For mobile money, use mobileSystem as bankName for API compatibility
        bankName: !isBankAccount ? formData.mobileSystem : formData.bankName,
        // For mobile money, set accountBranch to null
        accountBranch: !isBankAccount ? null : formData.branchCode,
        // For mobile money, use mobileNumber as accountNumber
        accountNumber: !isBankAccount ? formData.mobileNumber : formData.accountNumber,
        // For mobile money, clear accountType
        accountType: !isBankAccount ? '' : formData.accountType,
      };

      await addBankAccount(submitData);
      onSuccess();
      handleClose();
    } catch (error) {
      setApiError(
        error.response?.status === 409
          ? 'This account is already registered. Please check the details or use a different account.'
          : 'Failed to add account. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Resets form and closes dialog
  const handleClose = () => {
    setFormData({
      fullName: '',
      bankName: '',
      branchCode: '',
      accountNumber: '',
      accountType: '',
      accountCurrency: '',
      mobileNumber: '',
      mobileSystem: '',
    });
    setErrors({});
    setApiError(null);
    setLoading(false);
    setIsBankAccount(true);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 'bold',
          fontSize: '1.25rem',
          color: '#333',
          textAlign: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Add Financial Institution
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {apiError && (
          <Typography color="error" sx={{ mb: 2, textAlign: 'center' }}>
            {apiError}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
              Full Name
            </Typography>
            <TextField
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              placeholder="As registered on the account"
              error={!!errors.fullName}
              helperText={errors.fullName}
              sx={{
                backgroundColor: '#f0faff',
                borderRadius: '4px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#1976d2' },
                  '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                },
              }}
            />
          </Box>

          {/* Toggle System - Moved below Full Name */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 'bold' }}>
              {isBankAccount ? 'Bank Account' : 'Mobile Money'}
            </Typography>
            <FormControlLabel
              control={
                <IOSSwitch
                  checked={isBankAccount}
                  onChange={handleToggleChange}
                  color="primary"
                />
              }
              label=""
              sx={{ mr: 0 }}
            />
          </Box>
          {isBankAccount ? (
            <>
              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Financial Institution
                </Typography>
                <Autocomplete
                  freeSolo
                  options={bankData.banks}
                  value={formData.bankName}
                  onChange={handleBankNameChange}
                  onInputChange={(event, newInputValue) => {
                    setFormData((prev) => ({ ...prev, bankName: newInputValue || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search or select a financial institution"
                      error={!!errors.bankName}
                      helperText={errors.bankName}
                      sx={{
                        backgroundColor: '#f0faff',
                        borderRadius: '4px',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#e0e0e0' },
                          '&:hover fieldset': { borderColor: '#1976d2' },
                          '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                        },
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-popupIndicator': {
                      color: '#1976d2',
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Branch Code
                </Typography>
                <TextField
                  name="branchCode"
                  value={formData.branchCode}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  placeholder="Branch code"
                  error={!!errors.branchCode}
                  helperText={errors.branchCode}
                  sx={{
                    backgroundColor: '#f0faff',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#1976d2' },
                      '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Account Number
                </Typography>
                <TextField
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  placeholder="Recipient's bank account number"
                  error={!!errors.accountNumber}
                  helperText={errors.accountNumber}
                  sx={{
                    backgroundColor: '#f0faff',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#1976d2' },
                      '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Account Type
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel sx={{ color: '#666' }}>Select Account Type</InputLabel>
                  <Select
                    name="accountType"
                    value={formData.accountType}
                    onChange={handleChange}
                    label="Select Account Type"
                    error={!!errors.accountType}
                    sx={{
                      backgroundColor: '#f0faff',
                      borderRadius: '4px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                    }}
                  >
                    {accountTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.accountType && (
                    <Typography variant="caption" color="error">
                      {errors.accountType}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Account Currency
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel sx={{ color: '#666' }}>Select Currency</InputLabel>
                  <Select
                    name="accountCurrency"
                    value={formData.accountCurrency}
                    onChange={handleChange}
                    label="Select Currency"
                    error={!!errors.accountCurrency}
                    sx={{
                      backgroundColor: '#f0faff',
                      borderRadius: '4px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                    }}
                  >
                    {currencies.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.accountCurrency && (
                    <Typography variant="caption" color="error">
                      {errors.accountCurrency}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Mobile Money System
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <InputLabel sx={{ color: '#666' }}>Select Mobile Money System</InputLabel>
                  <Select
                    name="mobileSystem"
                    value={formData.mobileSystem}
                    onChange={handleMobileSystemChange}
                    label="Select Mobile Money System"
                    error={!!errors.mobileSystem}
                    sx={{
                      backgroundColor: '#f0faff',
                      borderRadius: '4px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                    }}
                  >
                    {mobileMoneySystems.map((system) => (
                      <MenuItem key={system} value={system}>
                        {system}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.mobileSystem && (
                    <Typography variant="caption" color="error">
                      {errors.mobileSystem}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Mobile Number
                </Typography>
                <TextField
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g., +254712345678"
                  error={!!errors.mobileNumber}
                  helperText={errors.mobileNumber}
                  sx={{
                    backgroundColor: '#f0faff',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#1976d2' },
                      '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body1" sx={{ mb: 1, color: '#666' }}>
                  Currency
                </Typography>
                <TextField
                  name="accountCurrency"
                  value={formData.accountCurrency}
                  fullWidth
                  variant="outlined"
                  placeholder="Auto-selected based on mobile money system"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                    },
                  }}
                  helperText="Currency is automatically set based on the mobile money system"
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            backgroundColor: '#009db9',
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            py: 1,
            px: 4,
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#009db9',
              border: '2px solid #009db9',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
            },
            '&.Mui-disabled': {
              backgroundColor: '#b0bec5',
              color: '#fff',
              border: '2px solid #b0bec5',
            },
          }}
        >
          {loading ? 'Adding...' : 'Add Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBankAccount;